/**
 * A aritmética da impressão de etiquetas — sem React, para poder ser testada
 * sozinha.
 *
 * A tela decide o que mostrar; quantas etiquetas saem, em quantas folhas e em
 * que posição de cada folha é conta, e conta é o que quebra em silêncio.
 *
 * ## O que ficou de fora, e por quê
 *
 * O protótipo tem saída térmica (ZPL), quatro modelos de etiqueta com
 * dimensão própria, fonte "Recebimento #1284" e modelos salvos. Nenhum dos
 * quatro entra aqui — os desvios estão registrados em `docs/de-para.md` com
 * a razão de cada um. O resumo: sem impressora térmica ligada em lugar
 * nenhum, um botão que promete enviar ZPL é um botão que mente.
 *
 * ## A grade é calculada, não é de um papel de catálogo
 *
 * As três grades abaixo dividem a área imprimível da A4 em partes iguais.
 * Elas NÃO reproduzem a geometria de uma referência comercial específica
 * (Avery L7654, Pimaco 6082 e afins), que tem margem e espaçamento próprios
 * de cada modelo. Casar com um papel específico exige medir aquele papel, e
 * papel de etiqueta errado por 1 mm arruína a folha inteira — quando alguém
 * trouxer o papel que a loja compra, a geometria dele entra como mais uma
 * entrada nesta lista, com o nome do fabricante.
 */
import type { LinhaSKU } from '../../plataforma/produtos'

/* ------------------------------------------------------------------ folha */

export const LARGURA_A4_MM = 210
export const ALTURA_A4_MM = 297

/**
 * A margem que o navegador não consegue imprimir na maioria das impressoras
 * jato e laser é de 5 mm a 6 mm. 10 mm dá folga para as duas.
 */
export const MARGEM_FOLHA_MM = 10

export type Folha = {
  chave: string
  rotulo: string
  colunas: number
  linhas: number
}

/**
 * A padrão vem nomeada, e não como `FOLHAS[0]`: índice de array é `T |
 * undefined` sob `noUncheckedIndexedAccess`, e a folha padrão é justamente a
 * que nunca pode faltar — é o fallback de `folhaPorChave`.
 */
export const FOLHA_PADRAO: Folha = {
  chave: 'a4-27',
  rotulo: 'A4 · 3 × 9 (27 por folha)',
  colunas: 3,
  linhas: 9,
}

export const FOLHAS: Folha[] = [
  FOLHA_PADRAO,
  { chave: 'a4-40', rotulo: 'A4 · 4 × 10 (40 por folha)', colunas: 4, linhas: 10 },
  { chave: 'a4-14', rotulo: 'A4 · 2 × 7 (14 por folha)', colunas: 2, linhas: 7 },
]

export function folhaPorChave(chave: string): Folha {
  return FOLHAS.find((f) => f.chave === chave) ?? FOLHA_PADRAO
}

export function etiquetasPorFolha(folha: Folha): number {
  return folha.colunas * folha.linhas
}

/**
 * A medida de UMA etiqueta, em milímetros, com uma casa decimal.
 *
 * Milímetro é a unidade do papel, e é a que o CSS de impressão usa: dizer ao
 * navegador `63.3mm` produz 63,3 mm no papel, enquanto pixel depende do DPI
 * que a impressora resolver usar.
 */
export function medidaDaEtiqueta(folha: Folha): { largura: number; altura: number } {
  const util = {
    largura: LARGURA_A4_MM - 2 * MARGEM_FOLHA_MM,
    altura: ALTURA_A4_MM - 2 * MARGEM_FOLHA_MM,
  }
  return {
    largura: Math.round((util.largura / folha.colunas) * 10) / 10,
    altura: Math.round((util.altura / folha.linhas) * 10) / 10,
  }
}

/* ------------------------------------------------------------- quantidade */

export type ModoDeQuantidade = 'estoque' | 'uma' | 'duas'

export const MODOS_DE_QUANTIDADE: { chave: ModoDeQuantidade; rotulo: string }[] = [
  { chave: 'estoque', rotulo: '= estoque' },
  { chave: 'uma', rotulo: '1 por SKU' },
  { chave: 'duas', rotulo: '2 por SKU' },
]

/**
 * Quantas etiquetas um SKU pede, antes de a pessoa digitar outra coisa.
 *
 * No modo "= estoque", saldo negativo vira ZERO e não vira um número
 * negativo de etiquetas: saldo negativo existe (é o rombo que o inventário
 * ainda não acertou) e não é uma instrução de impressão.
 */
export function quantidadePadrao(modo: ModoDeQuantidade, saldo: number): number {
  if (modo === 'uma') return 1
  if (modo === 'duas') return 2
  return saldo > 0 ? saldo : 0
}

export const MAX_ETIQUETAS_POR_SKU = 999

/**
 * O que a pessoa digitou na célula de quantidade, virando número.
 *
 * Texto vazio é zero e não `NaN`; o teto existe porque um zero a mais no
 * teclado numérico manda 9990 etiquetas para a fila de impressão.
 */
export function quantidadeDigitada(texto: string): number {
  const digitos = texto.replace(/\D/g, '')
  if (digitos === '') return 0
  return Math.min(Number(digitos), MAX_ETIQUETAS_POR_SKU)
}

/* --------------------------------------------------------------- seleção */

/** Um SKU escolhido, com quantas etiquetas dele vão sair. */
export type ItemSelecionado = {
  sku: LinhaSKU
  quantidade: number
}

export function totalDeEtiquetas(itens: ItemSelecionado[]): number {
  return itens.reduce((soma, item) => soma + item.quantidade, 0)
}

/**
 * Quantas folhas o trabalho ocupa, respeitando as posições já usadas.
 *
 * `comecarEm` é 1-indexado — é o que a pessoa lê no papel ("a próxima livre é
 * a nona"). Nada a imprimir são zero folhas, e não uma folha em branco.
 */
export function folhasNecessarias(total: number, comecarEm: number, porFolha: number): number {
  if (total <= 0) return 0
  const puladas = Math.max(1, comecarEm) - 1
  return Math.ceil((total + puladas) / porFolha)
}

/* ------------------------------------------------------------- impressão */

/** Uma etiqueta física — o SKU repetido tantas vezes quanto a quantidade. */
export type Etiqueta = {
  /** Único no trabalho inteiro: o mesmo SKU aparece N vezes. */
  chave: string
  sku: LinhaSKU
}

/**
 * Expande a seleção em uma etiqueta por peça.
 *
 * Duas camisetas pretas P são DUAS etiquetas iguais, e cada uma precisa da
 * própria posição na folha — é por isso que a expansão acontece aqui, e não
 * no `map` do JSX, onde a chave do React seria o código de barras repetido.
 */
export function expandirEtiquetas(itens: ItemSelecionado[]): Etiqueta[] {
  const etiquetas: Etiqueta[] = []
  for (const item of itens) {
    for (let i = 0; i < item.quantidade; i++) {
      etiquetas.push({ chave: `${item.sku.codigo_barras}-${i}`, sku: item.sku })
    }
  }
  return etiquetas
}

/**
 * As páginas prontas para desenhar: cada uma com exatamente `porFolha`
 * posições, e `null` onde não vai etiqueta.
 *
 * As posições anteriores a `comecarEm` são `null` de propósito — é assim que
 * a folha meio usada é aproveitada, e é o que a prévia mostra em cinza.
 */
export function paginarEtiquetas(
  etiquetas: Etiqueta[],
  comecarEm: number,
  porFolha: number,
): (Etiqueta | null)[][] {
  if (etiquetas.length === 0) return []

  const posicoes: (Etiqueta | null)[] = []
  for (let i = 1; i < Math.max(1, comecarEm); i++) posicoes.push(null)
  posicoes.push(...etiquetas)

  const paginas: (Etiqueta | null)[][] = []
  for (let i = 0; i < posicoes.length; i += porFolha) {
    const pagina = posicoes.slice(i, i + porFolha)
    while (pagina.length < porFolha) pagina.push(null)
    paginas.push(pagina)
  }
  return paginas
}

/* ------------------------------------------------- conteúdo da etiqueta */

export type CampoDaEtiqueta = 'logo' | 'nome' | 'variacao' | 'preco' | 'codigo' | 'barras'

export type Conteudo = Record<CampoDaEtiqueta, boolean>

/**
 * Os campos que a etiqueta pode carregar.
 *
 * O protótipo tem mais dois: "QR do SKU" e "Data de entrada". O QR pede um
 * codificador inteiro (a norma é bem maior que a do EAN-13) para repetir o
 * que o código de barras já diz. A data de entrada não existe: a entrada de
 * estoque é do LOTE, não do SKU, e não há coluna com essa data por peça.
 */
export const CAMPOS_DA_ETIQUETA: { chave: CampoDaEtiqueta; rotulo: string }[] = [
  { chave: 'logo', rotulo: 'Nome da loja' },
  { chave: 'nome', rotulo: 'Nome do produto' },
  { chave: 'variacao', rotulo: 'Cor e tamanho' },
  { chave: 'preco', rotulo: 'Preço de venda' },
  { chave: 'codigo', rotulo: 'Código do SKU' },
  { chave: 'barras', rotulo: 'Código de barras EAN-13' },
]

export const CONTEUDO_PADRAO: Conteudo = {
  logo: true,
  nome: true,
  variacao: true,
  preco: true,
  codigo: true,
  barras: true,
}

/**
 * A etiqueta precisa de ALGUMA identificação de máquina ou de gente.
 *
 * Desligar tudo produz um retângulo em branco — e o jeito de descobrir isso
 * não pode ser gastar uma folha de adesivo.
 */
export function conteudoVazio(conteudo: Conteudo): boolean {
  return !Object.values(conteudo).some(Boolean)
}
